import { useState, useImperativeHandle, forwardRef, useEffect } from "react";
import { Upload, Building, UserCheck, Eye, EyeOff } from "lucide-react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useUpdateFieldForce } from "@features/wx/fieldForce/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useRates } from "@features/wx/settings/hooks";

type PersonData = {
  id: string;
  fieldForceNo: string;
  name: string;
  role: string;
  department: string;
  area: string;
  contact: string;
  joiningDate: string;
  contactNumber: number;
  status: string;
  email?: string;
  imeiNumber?: string;
  password?: string;
  imei?: string;
  addressMap: {
    addressLine: string;
  };
};

type PreSelectedData = {
  department: string;
  role: string;
};

type BasicFormData = {
  fid: string;
  fieldForceNo: string;
  name: string;
  role: string;
  department: string;
  email: string;
  phone: number;
  imei: string;
  joinDate: string;
  address: string;
  password: string;
  confirmPassword: string;
  documentUpload?: File | null;
};

interface EditBasicInfoTabProps {
  preSelected?: PreSelectedData;
  initialPersonData?: PersonData | null;
  onSave?: (data: BasicFormData) => void;
}

export const EditBasicInfoTab = forwardRef<any, EditBasicInfoTabProps>(
  ({ preSelected, initialPersonData, onSave }, ref) => {
    const navigate = useNavigate();
    const { id, ff_no } = useParams<{ id: string; ff_no: string }>();
    const remoteUtilityId = useRemoteUtilityId();
    const updateFieldForce = useUpdateFieldForce(ff_no);
    const [formData, setFormData] = useState<BasicFormData>({
      fid: "",
      fieldForceNo: "",
      name: "",
      role: preSelected?.role || "",
      department: preSelected?.department || "Operations & Management",
      email: "",
      phone: 0,
      imei: "",
      joinDate: "",
      address: "",
      password: "",
      confirmPassword: "",
      documentUpload: null,
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Create form instance
    const formFields: FormField[] = [
      {
        name: "fieldForceNo",
        type: "text",
        label: "Field Force ID (FID)",
        placeholder: "FF-XXX",
        required: false,
        className: "col-span-1",
        classes: {
          container: "w-full mb-4 relative",
          label: "block text-sm font-medium text-gray-700 mb-1",
          input:
            "w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          error: "text-sm text-red-600 mt-1",
        },
        suffix: (
          <div
            className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
            style={{ top: "24px" }}
          >
            <Building className="h-4 w-4 text-gray-400" />
          </div>
        ),
      },
      {
        name: "name",
        type: "text",
        label: "Name",
        placeholder: "Full Name",
        required: false,
        className: "col-span-1",
      },
      {
        name: "department",
        type: "text",
        label: "Department",
        required: false,
        disabled: true,
        className: "col-span-1",
        classes: {
          container: "w-full mb-4 relative",
          label: "block text-sm font-medium text-gray-700 mb-1",
          input:
            "w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          error: "text-sm text-red-600 mt-1",
        },
        suffix: (
          <div
            className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
            style={{ top: "24px" }}
          >
            <Building className="h-4 w-4 text-gray-400" />
          </div>
        ),
      },
      {
        name: "role",
        type: "text",
        label: "Role",
        placeholder: "Enter role",
        required: false,
        disabled: true,
        className: "col-span-1",
        classes: {
          container: "w-full mb-4 relative",
          label: "block text-sm font-medium text-gray-700 mb-1",
          select:
            "w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
          error: "text-sm text-red-600 mt-1",
        },
        suffix: (
          <div
            className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
            style={{ top: "24px" }}
          >
            <UserCheck className="h-4 w-4 text-gray-400" />
          </div>
        ),
      },
      {
        name: "email",
        type: "email",
        label: "Email Address",
        placeholder: "email@example.com",
        required: false,
        className: "col-span-1",
      },
      {
        name: "phone",
        type: "number",
        label: "Phone Number",
        placeholder: "+1 (555) 123-4567",
        required: false,
        defaultValue: 0,
        className: "col-span-1",
      },
      {
        name: "password",
        type: showPassword ? "text" : "password",
        label: "Set Password",
        placeholder: "Enter password",
        required: false,
        className: "col-span-1",
        classes: {
          container: "w-full mb-4 relative",
          label: "block text-sm font-medium text-gray-700 mb-1",
          input:
            "w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          error: "text-sm text-red-600 mt-1",
        },
      },
      {
        name: "confirmPassword",
        type: showConfirmPassword ? "text" : "password",
        label: "Confirm Password",
        placeholder: "Confirm password",
        required: false,
        className: "col-span-1",
        classes: {
          container: "w-full mb-4 relative",
          label: "block text-sm font-medium text-gray-700 mb-1",
          input:
            "w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          error: "text-sm text-red-600 mt-1",
        },
      },
      {
        name: "imei",
        type: "number",
        label: "IMEI Number",
        placeholder: "15-digit IMEI number",
        required: false,
        className: "col-span-1",
      },
      {
        name: "joinDate",
        type: "datepicker",
        label: "Joining Date",
        required: false,
        className: "col-span-1",
      },
      {
        name: "address",
        type: "textarea",
        label: "Address",
        placeholder: "Enter full address",
        rows: 3,
        required: false,
        className: "col-span-2",
      },
      {
        name: "documentUpload",
        type: "file",
        label: "Document Upload",
        className: "col-span-2",
        accept: ".png,.jpg,.jpeg,.pdf",
        maxSize: 10 * 1024 * 1024,
        multiple: false,
        required: false,
        placeholder: "Upload ID document (PNG, JPG, PDF up to 10MB)",
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input: "hidden",
          error: "text-sm text-red-600 mt-1",
          helperText: "text-sm text-gray-500 text-center mt-1",
        },
        customRender: (field: FormField, form: any, error?: string) => {
          const value = form.watch(field.name);
          const onChange = (file: File | null) => {
            form.setValue(field.name, file);
          };

          return (
            <div className="w-full">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center bg-white hover:bg-gray-100 transition-colors cursor-pointer">
                <input
                  type="file"
                  accept={field.accept}
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    onChange(file);
                  }}
                  className="hidden"
                  id={`file-upload-${field.name}`}
                />
                <label
                  htmlFor={`file-upload-${field.name}`}
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="h-8 w-8 text-gray-400 mb-4" />
                  <span className="text-blue-500 font-medium hover:text-blue-600 transition-colors">
                    Upload ID document
                  </span>
                  <span className="text-sm text-gray-500 mt-1">
                    PNG, JPG, PDF up to 10MB
                  </span>
                </label>
                {value && (
                  <div className="mt-3 text-sm text-gray-600">
                    Selected: {value.name}
                  </div>
                )}
              </div>
              {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
            </div>
          );
        },
      },
    ];

    const form = FormService.createForm({
      fields: formFields,
      mode: "onChange",
      validateOnMount: false,
      defaultValues: formData,
    });

    const formatDateToYYYYDDMM = (
      date: string | Date | null | undefined
    ): string => {
      if (!date) return "";

      const dateObj = typeof date === "string" ? new Date(date) : date;
      if (!dateObj || isNaN(dateObj.getTime())) return "";

      const year = dateObj.getFullYear();
      const day = String(dateObj.getDate()).padStart(2, "0");
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");

      return `${year}-${day}-${month}`;
    };

    useEffect(() => {
      if (initialPersonData) {
        const personData = initialPersonData as PersonData;
        const newFormData = {
          fid: personData.id,
          fieldForceNo: personData.fieldForceNo,
          name: personData.name,
          role: personData.role,
          department: personData.department,
          email: personData.email || "",
          phone: personData.contactNumber || 0,
          password: personData.password,
          imei: personData.imeiNumber || "",
          joinDate: formatDateToYYYYDDMM(personData.joiningDate),
          address: personData?.addressMap?.addressLine || "",
          confirmPassword: personData.password,
          documentUpload: null,
        };

        // Update state
        setFormData(newFormData);

        // Patch form values individually
        form.setValue("fid", newFormData.fid);
        form.setValue("fieldForceNo", newFormData.fieldForceNo);
        form.setValue("name", newFormData.name);
        form.setValue("role", newFormData.role);
        form.setValue("department", newFormData.department);
        form.setValue("email", newFormData.email);
        form.setValue("phone", newFormData.phone);
        form.setValue("password", newFormData.password);
        form.setValue("imei", newFormData.imei);
        form.setValue("joinDate", newFormData.joinDate);
        form.setValue("address", newFormData.address);
        form.setValue("confirmPassword", newFormData.confirmPassword);
        form.setValue("documentUpload", newFormData.documentUpload);
      }
    }, [initialPersonData, form]);

    const handleFieldChange = (fieldName: string, value: any) => {
      setFormData((prev) => ({
        ...prev,
        [fieldName]: value,
      }));

      if (fieldName === "documentUpload" && value instanceof File) {
        const allowedTypes = [
          "image/png",
          "image/jpeg",
          "image/jpg",
          "application/pdf",
        ];
        const maxSize = 10 * 1024 * 1024;

        if (!allowedTypes.includes(value.type)) {
          console.error(
            "Invalid file type. Please upload PNG, JPG, or PDF files only."
          );
          return;
        }
        if (value.size > maxSize) {
          return;
        }
      }
    };

    const handleSubmit = (data: any) => {
      const isValid = FormService.validateForm(form);

      if (!isValid) {
        return;
      }

      let payload = FormService.createPayload(data, formFields);

      const formData = new FormData();
      formData.append("field_force_no", data.fieldForceNo);
      formData.append("name", data.name);
      formData.append("department", data.department);
      formData.append("role", data.role);
      formData.append("email", data.email);
      formData.append("contact_number", data.phone);
      formData.append("password", data.password);
      formData.append("confirmPassword", data.confirmPassword);
      formData.append("imei_number", data.imei || "");
      formData.append("joining_date", data.joinDate);
      if (data.documentUpload instanceof File) {
        formData.append("file", data.documentUpload);
      }
      formData.append("remote_utility_id", remoteUtilityId);
      formData.append(
        "address_map",
        JSON.stringify({ address_line: data.address || "" })
      );
      formData.append("is_field_force_info", "true");
      updateFieldForce.mutateAsync(formData);
      navigate(
        `/service-orders/field-force/edit/${id}/${ff_no}?tab=other-details`,
        { replace: true }
      );
    };

    const handleReset = () => {
      navigate(`/service-orders/field-force/`);
    };

    return (
      <div className="space-y-6">
        <DynamicForm
          fields={formFields}
          form={form}
          onSubmit={handleSubmit}
          onReset={handleReset}
          onFieldChange={handleFieldChange}
          gridColumns={2}
          responsiveColumns={{
            sm: 1,
            md: 2,
          }}
          config={{
            showResetButton: true,
            showSubmitButton: true,
            submitButtonText: initialPersonData
              ? "Update Technician"
              : "Create Technician",
            resetOnSubmit: false,
            resetButtonText: "Cancel",
            spacing: "comfortable",
            classes: {
              form: "space-y-8",
              fieldGroup:
                "bg-white border border-gray-200 rounded-lg p-6 shadow-sm",
              button: "px-6 py-2 rounded-md font-medium transition-colors",
              submitButton:
                "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50",
              resetButton:
                "border border-gray-300 text-gray-700 bg-white hover:bg-gray-50",
              buttonContainer:
                "flex justify-end gap-3 pt-6 border-t border-gray-200 mt-8",
              input:
                "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
              textarea:
                "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
              select:
                "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
              label: "block text-sm font-medium text-gray-700 mb-1",
              error: "text-sm text-red-600 mt-1",
              helperText: "text-sm text-gray-500 mt-1 flex",
              checkbox:
                "h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500",
              container: "space-y-1",
            },
          }}
        />
      </div>
    );
  }
);
